/**
 * Client mirror of functions/src/fde/productSurfaces/surfaceCatalog.js.
 * Keep catalogs aligned when adding products or surfaces.
 */

export const PRODUCT_SURFACE_CATALOG = [
  {
    productId: "cmr",
    label: "CMR (MasterChart)",
    surfaces: [
      {
        productId: "cmr",
        surfaceId: "medications",
        label: "Medication list",
        description: "Read and update the patient's MasterChart medication list.",
        defaultAdapterKey: "cmr.medications.reconcile",
      },
      {
        productId: "cmr",
        surfaceId: "demographics",
        label: "Demographics",
        description: "Patient registration and demographic fields.",
        defaultAdapterKey: null,
      },
      {
        productId: "cmr",
        surfaceId: "vitals",
        label: "Vital signs",
        description: "Documented vital signs on the patient record.",
        defaultAdapterKey: null,
      },
      {
        productId: "cmr",
        surfaceId: "history",
        label: "History / intake",
        description: "Pre-visit and historical clinical context on the record.",
        defaultAdapterKey: null,
      },
    ],
  },
  {
    productId: "chartmind",
    label: "ChartMind",
    surfaces: [
      {
        productId: "chartmind",
        surfaceId: "notes",
        label: "Clinical notes / HPI",
        description: "ChartMind note and HPI drafting surfaces.",
        defaultAdapterKey: null,
      },
      {
        productId: "chartmind",
        surfaceId: "diagnosis",
        label: "Diagnosis support",
        description: "Differential / diagnosis decision-support panels.",
        defaultAdapterKey: null,
      },
      {
        productId: "chartmind",
        surfaceId: "plan",
        label: "Treatment plan",
        description: "Plan and order-entry support panels.",
        defaultAdapterKey: null,
      },
      {
        productId: "chartmind",
        surfaceId: "education",
        label: "Patient education / discharge",
        description: "Education and discharge instruction drafting.",
        defaultAdapterKey: null,
      },
    ],
  },
  {
    productId: "carestart",
    label: "CareStart",
    surfaces: [
      {
        productId: "carestart",
        surfaceId: "conversation",
        label: "Guided conversation",
        description: "CareStart interview / conversation session capture.",
        defaultAdapterKey: null,
      },
    ],
  },
  {
    productId: "fusionmsi",
    label: "FusionMSI",
    surfaces: [
      {
        productId: "fusionmsi",
        surfaceId: "assist",
        label: "MSI assist",
        description: "FusionMSI assistance surface (future).",
        defaultAdapterKey: null,
      },
    ],
  },
];

export const PRODUCT_ID_TO_PRIMARY_LABEL = {
  cmr: "CMR",
  chartmind: "ChartMind",
  carestart: "CareStart",
  fusionmsi: "FusionMSI",
};

export const OUTPATIENT_TCI_PHASES = ["Pre-Visit", "Check-In", "Visit", "Treatment", "Follow-Up"];

export function surfaceKey(productId, surfaceId) {
  return `${productId}::${surfaceId}`;
}

export function getSurfaceDef(productId, surfaceId) {
  const product = PRODUCT_SURFACE_CATALOG.find((row) => row.productId === productId);
  return product?.surfaces.find((s) => s.surfaceId === surfaceId) || null;
}

export function derivePrimaryProducts(productSurfaces = []) {
  const labels = new Set();
  productSurfaces.forEach((row) => {
    const label = PRODUCT_ID_TO_PRIMARY_LABEL[row.productId];
    if (label) labels.add(label);
  });
  return [...labels];
}

export function formatProductSurfacesSummary(productSurfaces = []) {
  if (!productSurfaces.length) return "No sub-application surfaces bound.";
  return productSurfaces
    .map((row) => {
      const def = getSurfaceDef(row.productId, row.surfaceId);
      const product =
        PRODUCT_SURFACE_CATALOG.find((p) => p.productId === row.productId)?.label || row.productId;
      const surface = def?.label || row.surfaceId;
      return `${product}: ${surface} (${row.direction || "readwrite"})`;
    })
    .join("; ");
}

export function toggleProductSurface(list, productId, surfaceId, enabled) {
  const current = Array.isArray(list) ? [...list] : [];
  const idx = current.findIndex(
    (row) => row.productId === productId && row.surfaceId === surfaceId,
  );
  if (enabled && idx < 0) {
    const def = getSurfaceDef(productId, surfaceId);
    current.push({
      productId,
      surfaceId,
      direction: "readwrite",
      adapterKey: def?.defaultAdapterKey ?? null,
    });
  } else if (!enabled && idx >= 0) {
    current.splice(idx, 1);
  }
  return current;
}
